import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  Link,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Alert,
  Tooltip,
  ClickAwayListener,
  LinearProgress,
} from '@mui/material';
import {
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_PRIMARY_2_800,
  COLOR_TEXT_2,
  COLOR_BORDER,
  COLOR_BORDER_LIGHT,
} from 'src/utils/colors';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import TableChartIcon from '@mui/icons-material/TableChart';
import MapIcon from '@mui/icons-material/Map';
import { exchangeRates, HEADER_HEIGHT, MARKET_FINDER_TOOLTIP_CONTENT } from 'src/utils/const';
import {
  applySmartBoundsForCoords,
  convertCurrency,
  formatCurrency,
  formatMarketName,
  getCoordinatesFromWKT,
  getPoiPremiumColor,
} from 'src/utils/functions';
import LockIcon from '@mui/icons-material/Lock';
import { DataGrid, GridColDef, GridRow } from '@mui/x-data-grid';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { toggleSubmarketVisibility } from 'src/store/Dashboard/actions';
import { useSelector } from 'react-redux';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SubmarketFilter from './SubmarketFilter';
import SubmarketDashboardControl from './SubmarketDashboardControl';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import {
  checkIfMarketTrackedAlready,
  filterOutHiddenSubmarkets,
  formatPoiType,
  formatPremium,
  formatRadius,
  getSubmarketCoordsById,
  getSubmarketNameById,
  Poi,
  sortPois,
} from './helper';
import ComingSoon from 'src/App/Dashboards/Market/Pages/Features/SettingsV2/ComingSoon';
import TutorialModal from 'src/App/Dashboards/Market/Modals/TutorialModal';
import SubmarketMarketGrade from './SubmarketMarketGrade';
import _ from 'lodash';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { Search } from 'src/interfaces/idashboard';
import InfoOutlinedIcon from '@mui/icons-material/Info';
import theme from 'src/core/MuiThemeProvider/theme';
import ConfigurableTooltip from 'src/App/Dashboards/Market/Components/Tooltips/ConfigurableTooltip';
import RateLimitAlert from '../Alerts/RateLimitAlert';

const SubmarketsTab = ({
  currency,
  submarkets,
  subscriptionPlan,
  handleOnSampleClick,
  isMobileScreen,
  setVisibleSubmarkets,
  hoveredSubmarketId,
  setHoveredSubmarketId,
  visibleSubmarkets,
  handleHideSubmarket,
  marketID,
  hiddenSubmarkets,
  parentMarketDetails,
  setShowSubmarketCreationModal,
  setSubmarketCreationSource,
  MapComponent,
  submarketSortKey,
  setSubmarketSortKey,
  fetchSubMarketData,
  setSubmarkets,
  submarketBedroomType,
  setSubmarketBedroomType,
  isSubmarketAccessAllowed,
  setIsSubmarketAccessAllowed,
  setMapCenter,
  mapMode,
  heatmapMode,
  pois,
  hoveredPoiIndex,
  setHoveredPoiIndex,
  createSubmarketHideUnhideNotification,
  mapCopy,
  mapsCopy,
  newlyAddedSubmarketId,
  setNewlyAddedSubmarketId,
  isNewSubmarketDrawingMode,
  setIsNewSubmarketDrawingMode,
  showFullSubmarketMap,
  setSelectedGraphExportDetails,
  mobileHeaderHeight,
  showTutorial,
  setShowTutorial,
  setShowSubmarketMobileLegend,
  isSubmarketRateLimited,
}: {
  currency: string;
  submarkets;
  subscriptionPlan: string;
  handleOnSampleClick;
  isMobileScreen: boolean;
  setVisibleSubmarkets: (submarkets: any[]) => void;
  hoveredSubmarketId: string | null;
  setHoveredSubmarketId: React.Dispatch<React.SetStateAction<string | null>>;
  visibleSubmarkets: any[];
  handleHideSubmarket: (submarket_id: string) => void;
  marketID: number;
  hiddenSubmarkets: {
    data: string[];
    loading: boolean;
  };
  parentMarketDetails: {
    country: string;
    city: string;
    name: string;
    displayName: string | null;
  } | null;
  setShowSubmarketCreationModal: (show: boolean) => void;
  setSubmarketCreationSource: (source: 'submarkets-tab' | 'map') => void;
  MapComponent: any;
  submarketSortKey: string;
  setSubmarketSortKey: React.Dispatch<React.SetStateAction<string>>;
  fetchSubMarketData: () => void;
  setSubmarkets: (submarkets: any[]) => void;
  submarketBedroomType: string | null;
  setSubmarketBedroomType: (bedroomType: string | null) => void;
  isSubmarketAccessAllowed: boolean;
  setIsSubmarketAccessAllowed: (isSubmarketAccessAllowed: boolean) => void;
  setMapCenter: (mapCenter: { lat: number; lng: number }) => void;
  mapMode: string;
  heatmapMode: string;
  pois?: Poi[];
  hoveredPoiIndex?: number | null;
  setHoveredPoiIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  createSubmarketHideUnhideNotification: (message: string, severity: 'success' | 'error') => void;
  mapCopy: any;
  mapsCopy: any;
  newlyAddedSubmarketId: string | null;
  setNewlyAddedSubmarketId: React.Dispatch<React.SetStateAction<string | null>>;
  isNewSubmarketDrawingMode: boolean;
  setIsNewSubmarketDrawingMode: React.Dispatch<React.SetStateAction<boolean>>;
  showFullSubmarketMap: boolean;
  setSelectedGraphExportDetails: React.Dispatch<React.SetStateAction<any>>;
  mobileHeaderHeight: number;
  showTutorial: boolean;
  setShowTutorial: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSubmarketMobileLegend: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmarketRateLimited: boolean;
}) => {
  const dispatch = useDispatch();
  const search = useSelector((state: any) => state.dashboard.search);
  const currencySymbol = exchangeRates[currency].symbol;
  const ROW_HEIGHT = 52;
  const ROWS_PER_PAGE = 100;
  const [page, setPage] = useState(0);
  const [showHiddenDrawer, setShowHiddenDrawer] = useState(false);
  const [mobileViewMode, setMobileViewMode] = useState<'map' | 'table'>('map');

  const isHotspotMode = heatmapMode === 'hotspot';
  const sortedPois = useMemo(() => sortPois(pois || []), [pois]);
  const poiRows = useMemo(
    () => sortedPois.map((poi, index) => ({ ...poi, id: index + 1, pin: index + 1 })),
    [sortedPois]
  );

 

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('open_submarket_page');

    const newUrl = `${window.location.pathname}${
      urlParams.toString() ? `?${urlParams.toString()}` : ''
    }`;
    window.history.replaceState({}, '', newUrl);
  }, []);

  useEffect(() => {
    if (
      !submarkets ||
      submarkets === 'loading' ||
      !Array.isArray(submarkets) ||
      submarkets.length === 0
    ) {
      return;
    }

    if (checkIfMarketTrackedAlready('submarkets_tab_viewed', marketID)) return;

    logAmplitudeEvent('submarkets_tab_viewed', {
      market_name:
        parentMarketDetails?.displayName ||
        parentMarketDetails?.name ||
        search.data.displayName ||
        search.data.name ||
        '',
      market_id: marketID,
      submarkets_count: submarkets?.length,
    });
  }, [submarkets]);

  useEffect(() => {
    if (submarkets && submarkets !== 'loading' && Array.isArray(submarkets)) {
      setVisibleSubmarkets([...submarkets]);

      if (newlyAddedSubmarketId) {
        setHoveredSubmarketId(newlyAddedSubmarketId);
      }

      if (newlyAddedSubmarketId && mapCopy) {
        const geometryObject = getSubmarketCoordsById(newlyAddedSubmarketId, visibleSubmarkets);

        const boundary = geometryObject?.boundary;
        const center: any = geometryObject?.center;
        if (center && mapMode === 'heatmap' && heatmapMode === 'submarkets') {
          setMapCenter({ lat: center.lat, lng: center.lng });
          const boundaryCoords = getCoordinatesFromWKT(boundary);
          applySmartBoundsForCoords(mapCopy, mapsCopy, boundaryCoords, 0);
        }
      }
    }
    return () => {
      setVisibleSubmarkets([]);
    };
  }, [submarkets, mapCopy]);

  useEffect(() => {
    if (hoveredSubmarketId && submarkets) {
      const index =
        submarkets && submarkets !== 'loading'
          ? filterOutHiddenSubmarkets(
              submarkets,
              hiddenSubmarkets.data,
              newlyAddedSubmarketId
            ).findIndex(s => s.submarket_id === hoveredSubmarketId)
          : -1;

      if (index !== -1) {
        const targetPage = Math.floor(index / ROWS_PER_PAGE);
        setPage(targetPage);

        // Wait for page change to complete before scrolling
        setTimeout(() => {
          if (!dataGridRef.current) return;
          const gridViewport = document.querySelector('.MuiDataGrid-virtualScroller');
          if (gridViewport) {
            const scrollPosition = (index % ROWS_PER_PAGE) * ROW_HEIGHT;
            gridViewport.scrollTo({
              top: scrollPosition - gridViewport.clientHeight / 2 + ROW_HEIGHT / 2,
              behavior: 'smooth',
            });
          }
        }, 0);
      }
    }
  }, [hoveredSubmarketId, submarkets]);

  useEffect(() => {
    if (!isHotspotMode || !hoveredPoiIndex) return;
    const gridViewport = document.querySelector('.MuiDataGrid-virtualScroller');
    if (gridViewport) {
      const scrollPosition = (hoveredPoiIndex - 1) * ROW_HEIGHT;
      gridViewport.scrollTo({
        top: scrollPosition - gridViewport.clientHeight / 2 + ROW_HEIGHT / 2,
        behavior: 'smooth',
      });
    }
  }, [hoveredPoiIndex, isHotspotMode]);

  const handleShowSubmarket = (submarket_id: string) => {
    dispatch(
      toggleSubmarketVisibility(
        marketID,
        [submarket_id],
        'show',
        (state: 'success' | 'error' = 'success') =>
          createSubmarketHideUnhideNotification(
            state === 'success'
              ? `${getSubmarketNameById(submarket_id, submarkets)} was unhidden successfully.`
              : `Failed to unhide ${getSubmarketNameById(submarket_id, submarkets)}.`,
            state === 'success' ? 'success' : 'error'
          )
      )
    );
  };

  const handleShowAllSubmarkets = () => {
    setVisibleSubmarkets(submarkets);
    dispatch(
      toggleSubmarketVisibility(
        marketID,
        [],
        'show_all',
        (state: 'success' | 'error' = 'success') =>
          createSubmarketHideUnhideNotification(
            state === 'success'
              ? 'All submarkets are unhidden successfully.'
              : 'Failed to unhide all submarkets.',
            state === 'success' ? 'success' : 'error'
          )
      )
    );
  };

  const handleExport = () => {
    if (!isSubmarketAccessAllowed) {
      handleOnSampleClick('expert');
      return;
    }

    const visibleRows = visibleSubmarkets;
    const visibleColumns = columns.filter(
      col =>
        col.field !== 'actions' &&
        col.field !== 'yoy_revenue_change' &&
        col.field !== 'total_number_of_bookings' &&
        col.field !== 'pct_of_luxury_rentals'
    );

    const flattenedRows = visibleRows
      .filter(row => !hiddenSubmarkets.data.includes(String(row.submarket_id)))
      .map(row => {
        const entry: Record<string, string | number> = {};

        visibleColumns.forEach(col => {
          let value: string | number = '';

          switch (col.field) {
            case 'grade':
              value = row.neighborhood_grade || '';
              break;

            case 'submarket_name':
              // Replace commas in submarket names to avoid CSV delimiter issues
              value = (row.submarket_name || '').replace(/,/g, ' - ');
              break;

            case 'adr':
            case 'revenue':
            case 'occupancy_rate':
            case 'supply':
              value = row[col.field] ?? '';
              break;

            default:
              value = row[col.field] ?? '';
          }

          entry[col.headerName || col.field] = value;
        });

        return entry;
      })
      .filter(row => Object.keys(row).length > 0);

    if (flattenedRows.length === 0) {
      return;
    }

    // Format the flattenedRows to be in CSV format (array of arrays)
    const headers = visibleColumns.map(col => col.headerName || col.field);
    const csvData = [
      headers,
      ...flattenedRows.map(row => headers.map(header => row[header] ?? '')),
    ];

    console.log('csvData: ', csvData);

    setSelectedGraphExportDetails({
      graphData: csvData,
      fieldName: 'submarkets-export',
      graphType: 'submarketsTable',
    });
  };

  const getIsRowHidden = (submarket_id: string) => {
    return hiddenSubmarkets.data.includes(String(submarket_id)) || false;
  };

  const columns: GridColDef[] =
    submarkets === 'loading'
      ? []
      : [
          {
            field: 'submarket_id',
            headerName: 'ID',
            flex: 1,
            minWidth: 0,
            renderCell: () => <></>,
            hide: true,
          },
          {
            field: 'rank',
            headerName: `Rank`,
            flex: 1,
            minWidth: 70,
            renderCell: params => {
              return (
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
                  {params.row.rank || '-'}
                </Typography>
              );
            },
          },
          {
            field: 'grade',
            flex: 1,
            minWidth: 100,
            valueGetter: (params) => params.row.neighborhood_grade || 'Z',            
            renderHeader: () => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ConfigurableTooltip config={MARKET_FINDER_TOOLTIP_CONTENT.market_grade}>
                  <Box sx={{ fontWeight: 500 }}>Grade</Box>
                </ConfigurableTooltip>
              </Box>
            ),
            renderCell: params => {
              return (
                <SubmarketMarketGrade
                  neighborhoodGrade={params.row.neighborhood_grade}
                  isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                  handleOnSampleClick={handleOnSampleClick}
                  verifiedTier={params.row.verified_tier}
                />
              );
            },
          },
          {
            field: 'submarket_name',
            headerName: 'Name',
            flex: 1,
            minWidth: 200,
            renderCell: params => (
              <Link
                target="_blank"
                href={params.row.submarket_url}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  color: COLOR_PRIMARY_2,
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                  <LocationOnIcon
                    fontSize="small"
                    sx={{ opacity: 0.7, color: COLOR_PRIMARY_2_800 }}
                  />
                </Typography>
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500, fontSize: '14px' }}>
                  {params.value}
                </Typography>
                <OpenInNewRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
                {params.row.verified_tier == 'C' && (
                  <Chip label="Custom" color="success" size="small" />
                )}
              </Link>
            ),
          },
          {
            field: 'supply',
            headerName: 'Active Listings',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              const value = params.value ? params.value : '-';
              return (
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
                  {formatCurrency(String(value))}
                </Typography>
              );
            },
          },
          {
            field: 'occupancy_rate',
            headerName: 'Occupancy Rate',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              const value = params.value ? `${params.value}%` : '-';
              return !isSubmarketAccessAllowed ? (
                <IconButton onClick={() => handleOnSampleClick('expert')}>
                  <LockIcon color="primary" />
                </IconButton>
              ) : (
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
              );
            },
          },
          {
            field: 'adr',
            headerName: 'Daily Rate',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              const value = params.value
                ? `${currencySymbol}${formatCurrency(
                    String(convertCurrency(currency, params.value))
                  )}`
                : '-';
              return !isSubmarketAccessAllowed ? (
                <IconButton onClick={() => handleOnSampleClick('expert')}>
                  <LockIcon color="primary" />
                </IconButton>
              ) : (
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
              );
            },
          },
          {
            field: 'revenue',
            headerName: 'Revenue Potential',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              const value = params.value
                ? `${currencySymbol}${formatCurrency(
                    String(convertCurrency(currency, params.value))
                  )}`
                : '-';
              return !isSubmarketAccessAllowed ? (
                <IconButton onClick={() => handleOnSampleClick('expert')}>
                  <LockIcon color="primary" />
                </IconButton>
              ) : (
                <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
              );
            },
          },
          {
            field: 'yoy_revenue_change',
            headerName: 'YoY Revenue Change',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              return <ComingSoon sx={{ color: COLOR_TEXT_2, fontSize: 'inherit' }} />;
            },
          },
          {
            field: 'total_number_of_bookings',
            headerName: 'Total Number of Bookings',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              return <ComingSoon sx={{ color: COLOR_TEXT_2, fontSize: 'inherit' }} />;
            },
          },
          {
            field: 'pct_of_luxury_rentals',
            headerName: 'Luxury Rentals',
            flex: 1,
            minWidth: 100,
            renderCell: params => {
              return <ComingSoon sx={{ color: COLOR_TEXT_2, fontSize: 'inherit' }} />;
            },
          },
          {
            field: 'actions',
            headerName: 'Actions',
            width: 70,
            renderCell: params => {
              return !getIsRowHidden(params.row.submarket_id) ? (
                <Tooltip title="Hide Submarket">
                  <IconButton
                    onClick={() => handleHideSubmarket(params.row.submarket_id)}
                    sx={{ color: COLOR_TEXT_2 }}
                  >
                    <VisibilityOffIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Show Submarket">
                  <IconButton
                    onClick={() => handleShowSubmarket(params.row.submarket_id)}
                    sx={{ color: COLOR_TEXT_2 }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ];

  const poiColumns: GridColDef[] = [
    {
      field: 'pin',
      headerName: '#',
      width: 60,
      renderCell: params => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: COLOR_PRIMARY_2_100,
            color: COLOR_PRIMARY_2,
            fontWeight: 600,
            fontSize: '12px',
            height: '24px',
            minWidth: '32px',
          }}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Hotspot',
      flex: 2,
      minWidth: 200,
      renderCell: params => {
        const premium = params.row.avg_premium_factor;
        const showFire = typeof premium === 'number' && premium > 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {showFire && (
              <WhatshotIcon
                fontSize="small"
                sx={{ color: COLOR_PRIMARY_2, opacity: 0.7, flexShrink: 0 }}
              />
            )}
            <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 600, fontSize: '14px' }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'poi_type',
      headerName: 'Type',
      flex: 1,
      minWidth: 130,
      renderCell: params => (
        <Chip
          label={formatPoiType(params.value)}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 500,
            fontSize: '12px',
            borderColor: COLOR_PRIMARY_2_100,
            color: COLOR_TEXT_2,
          }}
        />
      ),
    },
    {
      field: 'listing_count',
      headerName: 'Active Listings',
      flex: 1,
      minWidth: 100,
      renderCell: params => (
        <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
          {params.value != null ? formatCurrency(String(params.value)) : '-'}
        </Typography>
      ),
    },
    {
      field: 'radius_m',
      headerName: 'Radius',
      flex: 1,
      minWidth: 90,
      renderCell: params => (
        <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
          {formatRadius(params.value)}
        </Typography>
      ),
    },
    {
      field: 'avg_premium_factor',
      headerName: 'Location Premium',
      flex: 1,
      minWidth: 140,
      renderCell: params => {
        if (params.value === 'locked') {
          return (
            <IconButton onClick={() => handleOnSampleClick('expert')}>
              <LockIcon color="primary" />
            </IconButton>
          );
        }
        if (typeof params.value !== 'number') {
          return <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>-</Typography>;
        }
        const premiumColor = getPoiPremiumColor(params.value);
        return (
          <Chip
            label={formatPremium(params.value)}
            size="small"
            sx={{
              backgroundColor: `${premiumColor}1A`,
              color: premiumColor,
              fontWeight: 700,
              fontSize: '12px',
              border: `1px solid ${premiumColor}33`,
            }}
          />
        );
      },
    },
  ];

  if (!submarkets) {
    return (
      <Box
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        sx={{ height: '200px', width: '100%' }}
      >
        <Typography variant="body1" color="gray">
          No Submarkets available
        </Typography>
      </Box>
    );
  }

  const getHeaderTitle = () => {
    let country = '0';
    let city = '0';
    let district = '';
    if (parentMarketDetails) {
      country = parentMarketDetails.country
        ? parentMarketDetails.country?.replace(/\s/g, '-').toLocaleLowerCase()
        : '0';
      city = parentMarketDetails.city ? parentMarketDetails.city.toLocaleLowerCase() : '0';
      district = parentMarketDetails.name.toLocaleLowerCase();
    } else {
      country = search.data.country?.replace(/\s/g, '-').toLocaleLowerCase() || '0';
      city = search.data.city?.toLocaleLowerCase() || '0';
      district = search.data.name?.toLocaleLowerCase();
    }

    const displayName = parentMarketDetails
      ? formatMarketName(parentMarketDetails as Search)
      : formatMarketName(search.data);

    const marketUrl = `/airbnb-data/${country}/${city}/${district}`;

    return (
      <Link underline="hover" color="primary" href={marketUrl} target="_blank">
        {displayName}
      </Link>
    );
  };

  const dataGridRef = useRef<any>(null);

  const handleDataGridRowHover = useCallback(
    _.debounce((target: any) => {
      const id = target.getAttribute('data-id');

      if (id && getIsRowHidden(id)) return;

      id && setHoveredSubmarketId(id);
      const geometryObject = getSubmarketCoordsById(id, visibleSubmarkets);
    }, 300),
    [visibleSubmarkets, mapMode, heatmapMode, mapCopy, mapsCopy]
  );

  useEffect(() => {
    return () => {
      handleDataGridRowHover.cancel();
    };
  }, [handleDataGridRowHover]);

  const handleDataGridRowLeave = () => setHoveredSubmarketId(null);


  const MapLoader =
    submarkets === 'loading' ? <LinearProgress sx={{ width: '90%', mb: '20px', mx: 'auto' }} /> : <></>;

  const MobileStaticControlPanel = (
    <Box sx={{ position: 'relative', top: '0', left: '0', width: '100%', pl: 2 }}>
      {!isHotspotMode && (
        <Grid item xs={12}>
          <SubmarketFilter
            submarketSortKey={submarketSortKey}
            setSubmarketSortKey={setSubmarketSortKey}
            fetchSubMarketData={fetchSubMarketData}
            isSubmarketAccessAllowed={isSubmarketAccessAllowed}
            handleOnSampleClick={handleOnSampleClick}
            submarkets={submarkets}
            heatmapMode={heatmapMode}
          />
        </Grid>
      )}
      {!isHotspotMode && mobileViewMode === 'table' && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <SubmarketDashboardControl
            selectedBedroom={submarketBedroomType}
            setSelectedBedroom={setSubmarketBedroomType}
            hiddenSubmarkets={hiddenSubmarkets}
            setShowHiddenDrawer={setShowHiddenDrawer}
            setShowSubmarketCreationModal={setShowSubmarketCreationModal}
            setSubmarketCreationSource={setSubmarketCreationSource}
            fetchSubMarketData={fetchSubMarketData}
            isSubmarketAccessAllowed={isSubmarketAccessAllowed}
            handleOnSampleClick={handleOnSampleClick}
            submarkets={submarkets}
            handleExport={handleExport}
            isMobileScreen={isMobileScreen}
            heatmapMode={heatmapMode}
          />
        </Grid>
      )}
    </Box>
  );

  const DesktopMapView = (
    <Grid container xs={12} md={showFullSubmarketMap ? 12 : 6}>
      <ClickAwayListener onClickAway={() => setIsNewSubmarketDrawingMode(false)}>
        <Box
          sx={{
            borderRadius: '20px',
            width: showFullSubmarketMap ? '100%' : 'min(94dvh, 700px)',
            height: isMobileScreen ? 'auto' : 'min(94dvh, 700px)',
            position: 'relative',
          }}
        >
          {MapLoader}
          {MapComponent}
        </Box>
      </ClickAwayListener>
    </Grid>
  );

  const MobileMapView = (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <ClickAwayListener onClickAway={() => setIsNewSubmarketDrawingMode(false)}>
        <Box
          sx={{
            borderRadius: '20px',
            width: isMobileScreen ? '100%' : showFullSubmarketMap ? '100%' : 'min(94dvh, 700px)',
            position: 'relative',
          }}
        >
          {MapLoader}
          {MapComponent}
        </Box>
      </ClickAwayListener>
    </Grid>
  );

  const filteredRows = filterOutHiddenSubmarkets(
    visibleSubmarkets,
    hiddenSubmarkets.data,
    newlyAddedSubmarketId
  );

  const dataGridSx = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: `1px solid ${COLOR_BORDER}`,
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: isHotspotMode ? COLOR_PRIMARY_2_100 : 'inherit',
      borderBottom: `1px solid ${isHotspotMode ? COLOR_PRIMARY_2 + '33' : '#e0e0e0'}`,
      ...(isMobileScreen && {
        position: 'sticky',
        zIndex: theme.zIndex.mobileStepper - 1,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      color: isHotspotMode ? COLOR_PRIMARY_2 : COLOR_TEXT_2,
      fontSize: '13px',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: isHotspotMode ? `${COLOR_PRIMARY_2_100}88` : 'rgba(0, 0, 0, 0.04)',
    },
    '& .highlighted-row': {
      backgroundColor: `${COLOR_PRIMARY_2_100} !important`,
    },
    '& .MuiDataGrid-cell': {
      fontWeight: 500,
      borderBottom: `1px solid ${COLOR_BORDER_LIGHT}`,
    },
    '& .hidden-row': {
      filter: 'grayscale(100%) opacity(0.5)',
    },
    ...(isMobileScreen && {
      paddingLeft: '10px',
      '& .MuiDataGrid-main': {
        overflow: 'visible',
      },
      '& .MuiDataGrid-virtualScroller': {
        marginTop: '0px !important',
      },
      '& .MuiDataGrid-footerContainer': {
        marginBottom: '80px',
      },
    }),
  };

  const DataGridTable = isHotspotMode ? (
    <DataGrid
      ref={dataGridRef}
      rows={poiRows}
      columns={poiColumns}
      disableSelectionOnClick
      disableColumnSelector
      getRowId={row => row.id}
      getRowClassName={params =>
        params.row.id === hoveredPoiIndex ? 'highlighted-row' : ''
      }
      autoHeight={isMobileScreen}
      componentsProps={{
        row: {
          onMouseEnter: e => {
            const id = Number(e.currentTarget.getAttribute('data-id'));
            if (!isNaN(id) && setHoveredPoiIndex) setHoveredPoiIndex(id);
          },
          onMouseLeave: () => setHoveredPoiIndex && setHoveredPoiIndex(null),
        },
      }}
      density="compact"
      sx={dataGridSx}
    />
  ) : (
    <DataGrid
      ref={dataGridRef}
      rows={filteredRows}
      columns={columns}
      disableSelectionOnClick
      disableColumnSelector
      getRowId={row => row.submarket_id}
      page={page}
      onPageChange={newPage => setPage(newPage)}
      getRowClassName={params => {
        return getIsRowHidden(params.row.submarket_id)
          ? 'hidden-row'
          : params.row.submarket_id === hoveredSubmarketId
          ? 'highlighted-row'
          : '';
      }}
      autoHeight={isMobileScreen}
      componentsProps={{
        row: {
          onMouseEnter: e => {
            if (isNewSubmarketDrawingMode) return;
            const target = e.currentTarget;
            handleDataGridRowHover(target);
          },
          onMouseLeave: () => handleDataGridRowLeave(),
        },
      }}
      density="compact"
      sx={dataGridSx}
    />
  );

  const TableView = (
    <Grid
      container
      xs={12}
      md={6}
      sx={{ pl: isMobileScreen ? 0 : 2, ...(isMobileScreen && { pb: '200px' }) }}
    >
      <Box sx={{ width: '100%', height: 'calc(100% - 60px)' }}>
        <Grid container item xs={12} sx={{ mb: 2 }} justifyContent="space-between">
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '5px',
                ...(isMobileScreen && {
                  mb: 2,
                }),
              }}
            >
              {isHotspotMode ? (
                <>Hotspot Analysis: where guests pay a premium in {getHeaderTitle()}</>
              ) : (
                <>
                  Find the best neighborhoods to start an Airbnb in {getHeaderTitle()}
                  <TooltipNew
                    title=""
                    content="Click to see the 3 minutes tutorial video on how to use this feature."
                  >
                    <IconButton
                      color="primary"
                      onClick={() => setShowTutorial(true)}
                      sx={{ transform: 'translateY(2px)' }}
                    >
                      <HelpOutlineRoundedIcon />
                    </IconButton>
                  </TooltipNew>
                </>
              )}
            </Typography>
          </Grid>

          {!isHotspotMode && (
            <Grid item xs={12}>
              <SubmarketDashboardControl
                selectedBedroom={submarketBedroomType}
                setSelectedBedroom={setSubmarketBedroomType}
                hiddenSubmarkets={hiddenSubmarkets}
                setShowHiddenDrawer={setShowHiddenDrawer}
                setShowSubmarketCreationModal={setShowSubmarketCreationModal}
                setSubmarketCreationSource={setSubmarketCreationSource}
                fetchSubMarketData={fetchSubMarketData}
                isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                handleOnSampleClick={handleOnSampleClick}
                submarkets={submarkets}
                handleExport={handleExport}
                isMobileScreen={isMobileScreen}
                heatmapMode={heatmapMode}
              />
            </Grid>
          )}
        </Grid>
        {!isHotspotMode && (
          <SubmarketFilter
            submarketSortKey={submarketSortKey}
            setSubmarketSortKey={setSubmarketSortKey}
            fetchSubMarketData={fetchSubMarketData}
            isSubmarketAccessAllowed={isSubmarketAccessAllowed}
            handleOnSampleClick={handleOnSampleClick}
            submarkets={submarkets}
            heatmapMode={heatmapMode}
          />
        )}
        {submarkets === 'loading' ? (
          <Box
            display={'flex'}
            justifyContent="center"
            alignItems="center"
            sx={{ height: '400px', width: '100%' }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ borderRadius: '12px' }}
            />
          </Box>
        ) : (
          DataGridTable
        )}
      </Box>

      <Drawer anchor="right" open={showHiddenDrawer} onClose={() => setShowHiddenDrawer(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">
              Hidden Submarkets ({hiddenSubmarkets.loading ? '...' : hiddenSubmarkets.data.length})
            </Typography>
            <IconButton onClick={() => setShowHiddenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {hiddenSubmarkets.data.length > 0 ? (
            <>
              <Button
                variant="contained"
                fullWidth
                onClick={handleShowAllSubmarkets}
                sx={{ mb: 2 }}
              >
                Show All Submarkets
              </Button>
              <List>
                {submarkets &&
                  submarkets !== 'loading' &&
                  submarkets
                    .filter(s => hiddenSubmarkets.data.includes(String(s.submarket_id)))
                    .map(submarket => (
                      <ListItem
                        key={submarket.submarket_id}
                        secondaryAction={
                          <IconButton onClick={() => handleShowSubmarket(submarket.submarket_id)}>
                            <VisibilityIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={submarket.submarket_name} />
                      </ListItem>
                    ))}
              </List>
            </>
          ) : (
            <Typography color="text.secondary" align="center">
              No hidden submarkets
            </Typography>
          )}
        </Box>
      </Drawer>
    </Grid>
  );

  const MobileTableView = (
    <Grid container sx={{ mt: 2 }}>
      <Box
        sx={{
          width: '100%',
          height: `calc(100vh - ${mobileHeaderHeight}px - 230px)`,
        }}
      >
        {submarkets === 'loading' ? (
          <Box
            display={'flex'}
            justifyContent="center"
            alignItems="center"
            sx={{ height: '250px', width: '100%' }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ borderRadius: '12px' }}
            />
          </Box>
        ) : (
          DataGridTable
        )}
      </Box>
    </Grid>
  );

  const FloatingSwitch = (
    <Box
      sx={{
        position: 'fixed',
        bottom: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: 'auto',
      }}
    >
      <Button
        variant="contained"
        onClick={() => setMobileViewMode(mobileViewMode === 'map' ? 'table' : 'map')}
        sx={{
          background: COLOR_PRIMARY_2,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontWeight: 500,
          fontSize: '14px',
          '&:hover': {
            background: COLOR_PRIMARY_2,
            color: 'white',
          },
          cursor: 'pointer',
          borderRadius: '25px',
          px: 4,
        }}
      >
        {mobileViewMode === 'map' ? (
          <TableChartIcon sx={{ color: 'white', fontSize: '16px' }} />
        ) : (
          <MapIcon sx={{ color: 'white', fontSize: '16px' }} />
        )}
        {mobileViewMode === 'map' ? 'Table' : 'Heatmap'}
      </Button>
    </Box>
  );

  const MobileLegendButton = (
    <Box
      sx={{
        position: 'absolute',
        bottom: 20,
        left: '10px',
        zIndex: 1000,
        width: 'auto',
      }}
    >
      <IconButton
        onClick={() => {
          setShowSubmarketMobileLegend(true);
        }}
        sx={{
          background: 'white',
          boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
          borderRadius: '25px',
          padding: '0',
          cursor: 'pointer',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: '25px', color: COLOR_PRIMARY_2 }} />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Grid
        container
        sx={{
          pb: isMobileScreen ? '120px' : '20px',
        }}
        id="submarketContainerGrid"
      >
        {isSubmarketRateLimited && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
          >
            <RateLimitAlert />
          </Box>
        )}

        {!isSubmarketRateLimited &&
          (!submarkets || submarkets.length === 0) &&
          !(isHotspotMode && sortedPois.length > 0) && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
          >
            <Alert severity="warning" sx={{ width: '100%' }}>
              No Submarkets available for this market.
            </Alert>
          </Box>
        )}

        {!isSubmarketRateLimited && !isMobileScreen && (
          <Grid container sx={{ height: '80vh' }}>
            {/* Left Side */}
            {DesktopMapView}

            {/* Right Side */}
            {!showFullSubmarketMap && TableView}
          </Grid>
        )}

        {!isSubmarketRateLimited && isMobileScreen && (
          <Grid
            container
            sx={{
              position: 'absolute',
              top: `${mobileHeaderHeight}px`,
              left: '0',
              width: '100%',
            }}
          >
            {MobileStaticControlPanel}
            {mobileViewMode === 'map' && MobileLegendButton}
            {mobileViewMode === 'map' && MobileMapView}
            {mobileViewMode === 'table' && MobileTableView}
            {FloatingSwitch}
          </Grid>
        )}
      </Grid>

      {showTutorial && (
        <TutorialModal
          open={showTutorial}
          onClose={() => setShowTutorial(false)}
          type="neighborhood_research"
          noProgressBar={true}
        />
      )}
    </>
  );
};

export default SubmarketsTab;
